import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Star, Check, X, Eye, EyeOff, Loader2, MessageSquare, LayoutDashboard, Shirt, CalendarDays, Settings, Instagram, Upload, Image, Truck, Type, FolderTree, Tag, Sparkles, LogOut } from "lucide-react";
import { Button } from "../../components/ui/button";
import axios from "axios";
import { toast } from "sonner";
import AdminSidebar from "../../components/admin/AdminSidebar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminReviews() {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [actionLoading, setActionLoading] = useState(null);

  const fetchReviews = useCallback(async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.get(`${API}/admin/reviews`, { headers: { Authorization: `Bearer ${token}` } });
      setReviews(res.data.reviews || []);
    } catch (err) {
      console.error("[AdminReviews] fetch failed:", err);
      toast.error("Failed to load reviews");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  const handleModerate = async (reviewId, updates) => {
    setActionLoading(reviewId);
    try {
      const token = localStorage.getItem("adminToken");
      await axios.patch(`${API}/admin/reviews/${reviewId}`, updates, { headers: { Authorization: `Bearer ${token}` } });
      setReviews(prev => prev.map(r => r.id === reviewId ? { ...r, ...updates } : r));
      toast.success("Review updated");
    } catch { toast.error("Failed to update"); }
    finally { setActionLoading(null); }
  };

  const handleLogout = () => { localStorage.removeItem("adminToken"); navigate("/admin/login"); };

  const filtered = (reviews || []).filter(r => {
    if (filter === "pending") return !r.approved && r.visible !== false;
    if (filter === "approved") return r.approved;
    if (filter === "hidden") return r.visible === false;
    return true;
  });

  const pendingCount = (reviews || []).filter(r => !r.approved && r.visible !== false).length;

  return (
    <div className="min-h-screen bg-[#FAF8F0]">
      <AdminSidebar />

      <main className="lg:ml-64 p-6 md:p-10">
        <div className="max-w-[900px] mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl text-[#282828]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }} data-testid="admin-reviews-title">Customer Reviews</h1>
              <p className="text-[12px] text-[#676664]/50 mt-1">{(reviews || []).length} total · {pendingCount} pending approval</p>
            </div>
          </div>

          {/* Filter tabs */}
          <div className="flex gap-1 mb-5 bg-white border border-[#282828]/8 p-1 w-fit" data-testid="review-filter-tabs">
            {[
              { key: "all", label: "All" },
              { key: "pending", label: `Pending (${pendingCount})` },
              { key: "approved", label: "Approved" },
              { key: "hidden", label: "Hidden" },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                className={`px-4 py-1.5 text-[11px] tracking-[0.05em] uppercase transition-all ${filter === tab.key ? 'bg-[#9A7B4F] text-white' : 'text-[#676664]/60 hover:text-[#282828]'}`}
                data-testid={`filter-${tab.key}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Reviews */}
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#9A7B4F]" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16">
              <MessageSquare className="w-10 h-10 text-[#676664]/15 mx-auto mb-3" strokeWidth={1} />
              <p className="text-[13px] text-[#676664]/40">No reviews in this category</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="reviews-list">
              {filtered.map(review => {
                const date = new Date(review.created_at).toLocaleDateString("en-AU", { month: "short", day: "numeric", year: "numeric" });
                const isPending = !review.approved && review.visible !== false;
                const isHidden = review.visible === false;

                return (
                  <div key={review.id} className={`bg-white border p-4 ${isPending ? 'border-amber-200' : isHidden ? 'border-[#282828]/5 opacity-50' : 'border-[#282828]/8'}`} data-testid={`admin-review-${review.id}`}>
                    <div className="flex items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2.5 flex-wrap">
                          <span className="text-[13px] text-[#282828] font-medium">{review.customer_name}</span>
                          <div className="flex items-center gap-0.5">
                            {[1, 2, 3, 4, 5].map(s => (
                              <Star key={s} className={`w-3 h-3 ${s <= review.rating ? 'fill-[#9A7B4F] text-[#9A7B4F]' : 'text-[#282828]/10'}`} strokeWidth={1.5} />
                            ))}
                          </div>
                          <span className="text-[10px] text-[#676664]/35">{date}</span>
                          {isPending && <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[9px] font-medium uppercase tracking-wider rounded">Pending</span>}
                          {review.approved && <span className="px-2 py-0.5 bg-green-50 text-green-600 text-[9px] font-medium uppercase tracking-wider rounded">Approved</span>}
                          {isHidden && <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-[9px] font-medium uppercase tracking-wider rounded">Hidden</span>}
                        </div>
                        {review.review_text && <p className="text-[13px] text-[#5A5A5A] leading-relaxed mt-2">{review.review_text}</p>}
                        <p className="text-[10px] text-[#676664]/30 mt-1.5">Product: {review.outfit_name || review.outfit_id?.substring(0, 8)} · {review.customer_email || "No email"}</p>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {actionLoading === review.id ? (
                          <Loader2 className="w-4 h-4 animate-spin text-[#9A7B4F]" />
                        ) : (
                          <>
                            {!review.approved && (
                              <button onClick={() => handleModerate(review.id, { approved: true, visible: true })} className="p-1.5 text-green-400 hover:text-green-600 hover:bg-green-50 rounded" title="Approve" data-testid={`approve-${review.id}`}>
                                <Check className="w-4 h-4" strokeWidth={2} />
                              </button>
                            )}
                            {review.approved && (
                              <button onClick={() => handleModerate(review.id, { approved: false })} className="p-1.5 text-amber-400 hover:text-amber-600 hover:bg-amber-50 rounded" title="Unapprove" data-testid={`unapprove-${review.id}`}>
                                <X className="w-4 h-4" strokeWidth={2} />
                              </button>
                            )}
                            {review.visible !== false ? (
                              <button onClick={() => handleModerate(review.id, { visible: false })} className="p-1.5 text-[#676664]/30 hover:text-red-400 hover:bg-red-50 rounded" title="Hide" data-testid={`hide-${review.id}`}>
                                <EyeOff className="w-4 h-4" strokeWidth={1.5} />
                              </button>
                            ) : (
                              <button onClick={() => handleModerate(review.id, { visible: true })} className="p-1.5 text-[#676664]/30 hover:text-green-400 hover:bg-green-50 rounded" title="Show" data-testid={`show-${review.id}`}>
                                <Eye className="w-4 h-4" strokeWidth={1.5} />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
